'use client';

import { type ReactNode, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { AppConfig, OnchainKitProvider } from '@coinbase/onchainkit';
import { usePathname } from 'next/navigation';
import { createConfig, http, useAccount, WagmiProvider } from 'wagmi';
import { base, baseSepolia } from 'wagmi/chains';
import { baseAccount } from 'wagmi/connectors';
import { ThemeConfig, ThemeProvider } from '@cbhq/cds-web';
import { useMediaQuery } from '@cbhq/cds-web/hooks/useMediaQuery';
import { Box } from '@cbhq/cds-web/layout/Box';
import { MediaQueryProvider } from '@cbhq/cds-web/system/MediaQueryProvider';

import { ErrorProvider } from './contexts/ErrorContext';
import { customTheme } from './theme/customTheme';
import {
  initClientAnalytics,
  logAppOpenedOnce,
  logPageView,
  logWalletConnectedOnce,
} from './utils/analyticsEvents';

type ProvidersProps = {
  children: ReactNode;
};

type DynamicThemeProviderProps = {
  children: ReactNode;
};

const APP_MAX_WIDTH = 680;

/** Width of the app's centered content column. Shared so fixed overlays can line up with it. */
export const appColumnMaxWidth = { tablet: APP_MAX_WIDTH, desktop: APP_MAX_WIDTH } as const;

const appContainerStyle = { marginLeft: 'auto', marginRight: 'auto' } as const;

type AppContainerProps = {
  children: ReactNode;
};

function AppContainer({ children }: AppContainerProps) {
  return (
    <Box flexDirection="column" width="100%" maxWidth={appColumnMaxWidth} style={appContainerStyle}>
      {children}
    </Box>
  );
}

const onchainKitConfig: AppConfig = {
  appearance: {
    mode: 'auto',
  },
  wallet: {
    display: 'classic',
    preference: 'all',
  },
} as const;

const baseAccountConnector = baseAccount({
  appName: 'Base Verify',
  appLogoUrl: 'https://verify.base.dev/iconUrl.png',
  preference: { walletUrl: process.env.NEXT_PUBLIC_KEYS_URL },
});

const wagmiConfig = createConfig({
  chains: [base, baseSepolia],
  connectors: [baseAccountConnector],
  transports: {
    [base.id]: http(),
    [baseSepolia.id]: http(),
  },
  ssr: true,
});

function DynamicThemeProvider({ children }: DynamicThemeProviderProps) {
  const [mounted, setMounted] = useState(false);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  const activeColorScheme = useMemo(() => {
    return prefersDark ? 'dark' : 'light';
  }, [prefersDark]);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Don't render until we're mounted on the client
  if (!mounted) {
    return null;
  }

  return (
    <ThemeProvider
      theme={customTheme as unknown as ThemeConfig}
      activeColorScheme={activeColorScheme}
    >
      {children}
    </ThemeProvider>
  );
}

function WalletConnectedLogger() {
  const { address, isConnected } = useAccount();

  useEffect(() => {
    const isLocal = process.env.NEXT_PUBLIC_ANALYTICS_ENVIRONMENT === 'local';
    if (!isLocal && isConnected && address) {
      logWalletConnectedOnce(address);
    }
  }, [isConnected, address]);

  return null;
}

function PageViewLogger() {
  const pathname = usePathname();

  useEffect(() => {
    const isLocal = process.env.NEXT_PUBLIC_ANALYTICS_ENVIRONMENT === 'local';
    if (isLocal) return;
    logPageView(pathname);
  }, [pathname]);

  return null;
}

function UrlLogger() {
  const pathname = usePathname();

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_LOAD_ERUDA === 'true' && typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.log('Full URL:', window.location.href);
    }
  }, [pathname]);

  return null;
}

export function Providers({ children }: ProvidersProps) {
  // Initialize analytics before child effects can run
  useLayoutEffect(() => {
    if (typeof window !== 'undefined') {
      const isLocal = process.env.NEXT_PUBLIC_ANALYTICS_ENVIRONMENT === 'local';
      if (!isLocal) {
        initClientAnalytics();
      }
    }
  }, []);

  // Log app opened after init
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isLocal = process.env.NEXT_PUBLIC_ANALYTICS_ENVIRONMENT === 'local';
      if (!isLocal) {
        logAppOpenedOnce();
      }
    }
  }, []);

  return (
    <MediaQueryProvider>
      <DynamicThemeProvider>
        <ErrorProvider>
          <WagmiProvider config={wagmiConfig}>
            <OnchainKitProvider
              apiKey={process.env.NEXT_PUBLIC_ONCHAINKIT_API_KEY}
              chain={base}
              config={onchainKitConfig}
            >
              <WalletConnectedLogger />
              <PageViewLogger />
              <UrlLogger />
              <AppContainer>{children}</AppContainer>
            </OnchainKitProvider>
          </WagmiProvider>
        </ErrorProvider>
      </DynamicThemeProvider>
    </MediaQueryProvider>
  );
}
